import type { InspectionResult, TabletBlock, VocabularyEntry } from '@ghp/domain'
import { resolveInspectionResult, suggestRecommendations } from '@ghp/domain'
import { Button, Card, CardBody, InfoNote, SectionLabel, SegmentedControl, Textarea } from '@ghp/ui'
import { GitBranch, Hammer, MessageSquare, Sparkles, Undo2 } from 'lucide-react'
import { useId } from 'react'

import type { InspectionDraft } from '../../../offline/data-db'
import { RESULT_PRESENTATION } from '../labels'
import s from './InspectionTab.module.css'

/**
 * Onglet « Contrôle » — **ce que l'agent sait en fin de contrôle** : la conclusion, puis ses
 * observations (#411, décision du 09/10/2026), puis les travaux à réaliser (#345), qui se tirent
 * des anomalies et s'impriment avec la conclusion. Relever vient d'abord, dans l'onglet
 * « Relevé », qui porte depuis les bâtiments contrôlés, les anomalies relevées et les eaux de pluie
 * vers les eaux usées. La carte « Remarque sur les installations » (#316) a quitté la tablette : ses
 * remarques se relèvent en anomalies, en points d'eau (WC broyeur) ou ici (décompression).
 *
 * ⚠️ La conclusion **se surcharge** depuis #86 (M19) : le contrôle segmenté écrit dans
 * `draft.result`, et le geste de retour le remet à `null`. La dérivation n'est pas un repli
 * qu'on abandonne au premier clic — elle reprend la main dès que la place est rendue.
 */
export interface InspectionTabProps {
  draft: InspectionDraft
  /** Fiche transmise, ou éditeur monté sans voie d'écriture : tout se lit, rien ne se saisit. */
  disabled: boolean
  onDraftChange?: (patch: Partial<InspectionDraft>) => void
  /** Le référentiel en cache (M17) : les phrases de travaux que les anomalies appellent. */
  vocabulary?: readonly VocabularyEntry[]
  /** Ce que la règle du rapport ne demande plus (M33) — ici, « Travaux à réaliser ». */
  hiddenBlocks?: readonly TabletBlock[]
}

/** Les segments, **dérivés de la table des libellés** — pas une seconde liste à tenir. */
const RESULT_OPTIONS: readonly { value: InspectionResult; label: string }[] = (
  Object.keys(RESULT_PRESENTATION) as InspectionResult[]
).map((value) => ({ value, label: RESULT_PRESENTATION[value].label }))

export function InspectionTab({
  draft,
  disabled,
  onDraftChange,
  vocabulary = [],
  hiddenBlocks = [],
}: InspectionTabProps) {
  const notesId = useId()
  const { value, derived, overridden } = resolveInspectionResult(draft)

  return (
    <div className={s.stack}>
      <Card>
        <CardBody className={s.conclusion}>
          <SectionLabel>
            <GitBranch size={16} aria-hidden />
            Conclusion du contrôle
          </SectionLabel>

          {/*
           * `disabled` ne porte plus que le verrou de la fiche transmise (D4) : un contrôle
           * parti est immuable côté terrain, et c'est le seul cas où l'agent ne conclut pas.
           */}
          <SegmentedControl
            label="Conclusion du contrôle"
            options={RESULT_OPTIONS}
            value={value}
            disabled={disabled}
            onValueChange={(result) => onDraftChange?.({ result })}
          />
          {!overridden && (
            <InfoNote icon={<Sparkles size={15} />}>
              Déduite des anomalies relevées et des eaux de pluie vers les eaux usées : un élément
              technique « Non raccordé » ou « Refus » conclut <b>non raccordé</b>, toute autre
              anomalie <b>partiellement raccordé</b>. Corrigez-la si le terrain dit autre chose —
              c'est votre choix qui sera transmis.
            </InfoNote>
          )}

          {/*
           * ⚠️ Le geste de retour n'est pas un ornement : sans lui, le premier clic gèle la
           * conclusion pour de bon, et l'anomalie relevée dix minutes plus tard ne la déplace
           * plus. L'agent n'a alors aucun moyen de rendre la main à la règle — reprendre la
           * valeur déduite au doigt écrirait une surcharge de plus, pas son absence.
           *
           * Montré dès qu'une conclusion est posée à la main, même identique à la déduction :
           * ce que le bouton rend, c'est le calcul, pas une autre valeur.
           */}
          {overridden && (
            <div className={s.override}>
              <InfoNote icon={<Sparkles size={15} />} tone={value === derived ? 'neutral' : 'warn'}>
                Conclusion posée à la main
                {value === derived
                  ? ' — elle rejoint ce que les anomalies relevées déduisent.'
                  : ` : les anomalies relevées déduisent « ${RESULT_PRESENTATION[derived].label} ».`}
              </InfoNote>
              <Button
                tone="ghost"
                size="sm"
                disabled={disabled}
                onClick={() => onDraftChange?.({ result: null })}
              >
                <Undo2 size={15} aria-hidden />
                Revenir à la déduction
              </Button>
            </div>
          )}

          <SectionLabel className={s.observations}>
            <MessageSquare size={16} aria-hidden />
            <label htmlFor={notesId}>Observations agent</label>
          </SectionLabel>
          <Textarea
            id={notesId}
            rows={3}
            disabled={disabled}
            // La décompression n'a plus de case (#411) : elle s'écrit ici, imprimée au rapport.
            placeholder="Ce que le relevé ne dit pas, imprimé au rapport : décompression, accès, écoulement vérifié…"
            value={draft.notes ?? ''}
            onValueChange={(notes) => onDraftChange?.({ notes })}
          />
        </CardBody>
      </Card>

      {!hiddenBlocks.includes('recommendations') && (
        <Recommendations
          written={draft.recommendations}
          proposed={suggestRecommendations(draft, vocabulary)}
          disabled={disabled}
          onChange={(recommendations) => onDraftChange?.({ recommendations })}
        />
      )}
    </div>
  )
}

/**
 * Carte « Travaux à réaliser » (#345) — ce que le rapport imprime sous ce titre. Elle **propose**
 * les travaux que le relevé appelle (`suggestRecommendations`) tant que l'agent n'y a pas touché,
 * puis montre son texte, même vidé : la proposition ne repasse jamais par-dessus. Le geste de
 * retour lui rend la main, comme celui de la conclusion.
 */
function Recommendations({
  written,
  proposed,
  disabled,
  onChange,
}: {
  written: string | null | undefined
  proposed: string
  disabled: boolean
  onChange: (recommendations: string | null) => void
}) {
  const id = useId()
  const overridden = written != null

  return (
    <Card>
      <CardBody className={s.works}>
        <SectionLabel>
          <Hammer size={16} aria-hidden />
          <label htmlFor={id}>Travaux à réaliser</label>
        </SectionLabel>
        {/* Cinq lignes : une proposition ordinaire se lit sans défiler sur la tablette. */}
        <Textarea
          id={id}
          rows={5}
          disabled={disabled}
          placeholder="Une ligne par travail à prescrire au propriétaire."
          value={written ?? proposed}
          onValueChange={onChange}
        />
        {!overridden && proposed !== '' && (
          <InfoNote icon={<Sparkles size={15} />}>
            Proposés d’après le relevé et les anomalies relevées. Modifiez-les ou retirez-les —
            c’est votre texte qui sera transmis.
          </InfoNote>
        )}
        {overridden && (
          <div className={s.override}>
            <Button tone="ghost" size="sm" disabled={disabled} onClick={() => onChange(null)}>
              <Undo2 size={15} aria-hidden />
              Revenir aux travaux proposés
            </Button>
          </div>
        )}
      </CardBody>
    </Card>
  )
}
